import { useCallback, useEffect, useState } from "react";
import { getSchoolOverview } from "../../api/analyticsApi";
import type { OverviewQuery, SchoolOverview } from "./overviewTypes";

interface State { key: string; loading: boolean; data: SchoolOverview | null; error: string | null }

/** Actor scope includes role/lifecycle; it is a client isolation key, never request authority. */
export function useSchoolOverview(actorScope: string, organisationId: string, query: OverviewQuery) {
  const { period, department, initiativeId } = query;
  const [generation, setGeneration] = useState(0);
  const key = JSON.stringify([actorScope, organisationId, period, department, initiativeId, generation]);
  const [state, setState] = useState<State>({ key: "", loading: true, data: null, error: null });
  const refresh = useCallback(() => setGeneration(value => value + 1), []);

  useEffect(() => {
    if (!actorScope || !organisationId) return;
    let cancelled = false;
    const requestKey = JSON.stringify([actorScope, organisationId, period, department, initiativeId, generation]);
    setState({ key: requestKey, loading: true, data: null, error: null });
    getSchoolOverview(organisationId, { period, department, ...(initiativeId ? { initiativeId } : {}) }).then(
      data => { if (!cancelled) setState({ key: requestKey, loading: false, data, error: null }); },
      error => { if (!cancelled) setState({ key: requestKey, loading: false, data: null, error: error instanceof Error ? error.message : "The overview could not be loaded. Please try again." }); },
    );
    return () => { cancelled = true; };
  }, [actorScope, organisationId, period, department, initiativeId, generation]);

  useEffect(() => {
    const onActivate = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", onActivate);
    document.addEventListener("visibilitychange", onActivate);
    return () => { window.removeEventListener("focus", onActivate); document.removeEventListener("visibilitychange", onActivate); };
  }, [refresh]);

  // Effects run after render: never expose a previous scope's success or refusal meanwhile.
  if (state.key !== key || !actorScope || !organisationId) return { loading: true, data: null, error: null, refresh };
  return { loading: state.loading, data: state.data, error: state.error, refresh };
}
